// src/com/trans_plan_tsr/TransPlanTsr.js
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/system';
import { IconButton, CircularProgress, Button } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { useSnackbar } from 'notistack';
import {
    ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { AppMenuUrl, AppObject, AppStyle, TopCode } from '../../value/AppConst';
import {
    arriveInfoListToSelectStoreRepo, bulkCompanyProductsListRepo,menuRequestRepo,menuTreeRepo,transPlanLaunchListParamRepo,transPlanTsrListParamRepo, transPlanTsrListRepo, userInfoRepo
} from '../../value/AppModel';
import { LIST_TRANS_PLAN_TSR } from './TransPlanTsrGql';
import { TransPlanTsrDictionary } from './TransPlanTsrDict';
import { useAlertSetter } from '../../api/app-hook';
import { LIST_ARRIVE_INFO_TO_SELECT_STORE } from '../arrive_info/ArriveInfoGql';
import ValueMisc from '../../value/ValueMisc';
import AutoTextField from '../../common/AutoTextField';
import TransPlanTsrItem from './TransPlanTsrItem';
import VzmDateField from '../../common/VzmDateField';
import IsValid from '../../value/IsValid';
import StoreHouseSvg from '../../asset/image/store_house.svg';
import {
    LIST_BULK_COMPANY_PRODUCTS
} from '../bulk_company_products/BulkCompanyProductsGql';

/**
 * (TRANS_PLAN_TSR) 정보 View Controller
 * VIEW
 * TSR 발행 메뉴 선택하면 나오는 화면. 운송사, 점포는 볼 수 없는 화면.
 * 과거 프로젝트의 TAB_TransPlan.js
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-01-02 14:59:38 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function TransPlanTsr() {
    const menuTree = useReactiveVar(menuTreeRepo);
    const sessionInfo = useReactiveVar(userInfoRepo);
    const storeList = useReactiveVar(arriveInfoListToSelectStoreRepo);
    const transPlanTsrListParam = useReactiveVar(transPlanTsrListParamRepo);
    const transPlanTsrListData = useReactiveVar(transPlanTsrListRepo);
    const bulkCompanyProductsListData = useReactiveVar(bulkCompanyProductsListRepo);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getTransPlanTsrList, responseList] = useLazyQuery(LIST_TRANS_PLAN_TSR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: TransPlanTsrDictionary.errorTitle.Get, message: [error.message]})
    });
    // Get store list from arrive_info table.
    const [getArriveInfoList, responseStoreList] = useLazyQuery(LIST_ARRIVE_INFO_TO_SELECT_STORE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreList(data, option)},
        onError: (error) => addAlert({title: '도착지 목록 조회 오류', message: [error.message]})
    });
    // 982 Bulk 상품 목록 조회
    const [getBulkCompanyProductsList, responseBulkCompanyProductsList] = useLazyQuery(LIST_BULK_COMPANY_PRODUCTS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetBulkCompanyProductsList(data, option)},
		onError: (error) => addAlert({title: '벌크상품 목록 조회 오류', message: [error.message]})
    });

    const getTransPlanTsrListNow = (storeId, planDate) => {
        const dcCd = sessionInfo.user.dscIdAs;
        if(!planDate) {
            planDate = ValueMisc.getYMD();
        }
        const param = {variables:{dcCd:dcCd, storeId:storeId, planDate:planDate}};
        getTransPlanTsrList(param);
    };

    // 날짜, 점포 변경 시 TSR 조회 호출
    // 다른 화면에서 transPlanTsrListParam 설정하고 이 화면으로 들어오면 바로 조회 가능함.
    useEffect(() => {
        if(transPlanTsrListParam.storeId && transPlanTsrListParam.planDate) {
            getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate);
        }
        else if(!transPlanTsrListParam.planDate) {
            const newParam = {...transPlanTsrListParam, planDate:ValueMisc.getYMD()};
            transPlanTsrListParamRepo(newParam);
        }
    }, [transPlanTsrListParam]);

    // 먼저 Store 목록을 가져온 후 transPlanTsrListParam의 내용에 따라서 최초 LIST_TRANS_PLAN_TSR 호출
    useEffect(() => {
        const diff = new Date().getTime()/1000 - storeList.when;
        if(diff > 300) {
            getArriveInfoList({variables:{dcCd:sessionInfo.user.dscIdAs}});
        }
    }, []);

    const getBulkCompanyProductsListNow = () => {
        const diff = new Date().getTime()/1000 - bulkCompanyProductsListData.when;
        if(diff > 3600) {
            getBulkCompanyProductsList({variables:{bulkCompanyId:null, bulkProductsId:null}});
        }
    };

    // 982 Bulk 상품 목록 조회
    useEffect(()=>{
        if(sessionInfo.user.dscIdAs === TopCode.DcCode.Adc982) {
            getBulkCompanyProductsListNow();
        }
    }, [sessionInfo]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        lastReloadRef.current = new Date().getTime()/1000;
        if(data.transPlanTsrList) {
            transPlanTsrListRepo({when:new Date().getTime()/1000, records:data.transPlanTsrList});
        }
    };
    
    const onCompleteGetStoreList = (data, clientOption) => {
        if(data.arriveInfoList) {
            if(data.arriveInfoList.length > 0) {
                // sort by storeId (text)
                data.arriveInfoList.sort((a,b) => {
                    if(a.storeId < b.storeId) return -1;
                    if(a.storeId > b.storeId) return 1;
                    return 0;
                });
                if(!transPlanTsrListParam.storeId) {
                    const newParam = {...transPlanTsrListParam, storeId:data.arriveInfoList[0].storeId};
                    transPlanTsrListParamRepo(newParam);
                }
            }
            arriveInfoListToSelectStoreRepo({
                when:new Date().getTime()/1000,
                records:data.arriveInfoList
            });
        }
    };

    // 982 Bulk 상품 목록 조회
    const onCompleteGetBulkCompanyProductsList = (data, clientOption) => {
        if(data.bulkCompanyProductsList) {
            bulkCompanyProductsListRepo({when:new Date().getTime()/1000, records:data.bulkCompanyProductsList});
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 10) {
            // Too short to reload
            enqueueSnackbar('10초 이내에 재조회는 지양해 주세요.', {variant:'info', autoHideDuration:2000});
        }
        else if(transPlanTsrListParam.storeId && transPlanTsrListParam.planDate) {
            getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate);
        }
    };
    
    const onStoreSelected = (id, value) => {
        const newParam = {...transPlanTsrListParam, storeId:value};
        transPlanTsrListParamRepo(newParam);
    };

    const onChangePlanDate = (id, value) => {
        const newParam = {...transPlanTsrListParam, planDate:value};
        transPlanTsrListParamRepo(newParam);
    };

    const onCompleteTsrMutation = () => {
        getTransPlanTsrListNow(transPlanTsrListParam.storeId, transPlanTsrListParam.planDate);
    };
    
        // 배차요청 화면으로 이동하기.
    const onClickToMoveToTransPlanRequest = () => {
        if(sessionInfo.isHomeplusOrEtrace() && transPlanTsrListParam.planDate && transPlanTsrListParam.storeId) {
            const menuSet = ValueMisc.findMenu(AppMenuUrl.TransPlan, menuTree);
            if(!menuSet.menu) {
                addAlert({
                    title: '운송요청 화면 이동',
                    message: ['운송요청 화면이 없습니다. 관리자에게 문의해 주세요.'],
                });
                return;
            }
            transPlanLaunchListParamRepo({
                planDate: transPlanTsrListParam.planDate,
                storeId: transPlanTsrListParam.storeId,
            });
            menuRequestRepo(menuSet);
        }
    };

    const loadingAny
        = responseList.loading // || responseItem.loading
        || responseStoreList.loading
        || responseBulkCompanyProductsList.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <VzmDateField
                        dict={{
                            id: 'planDate',
                            label: '출고일자'
                        }}
                        value={transPlanTsrListParam.planDate || ''}
                        error={!IsValid.date(transPlanTsrListParam.planDate)}
                        onChange={onChangePlanDate}
                    />
                    <AutoTextField
                        dict={{
                            id:'storeId',
                            label:'도착지',
                            labelKey:'storeName',
                            getOptionLabel: (opt) => opt.storeName + ' [' + opt.storeId + ']'
                        }}
                        value={transPlanTsrListParam.storeId || ''}
                        list={storeList.records}
                        disabled={responseStoreList.loading}
                        style={{minWidth:300, marginLeft: 10}}
                        onChange={onStoreSelected}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    <Button
                        color={AppStyle.mui.inherit}
                        variant={AppStyle.mui.contained}
                        startIcon={<CardGiftcardIcon />}
                        onClick={onClickToMoveToTransPlanRequest}
                        style={{marginLeft:10}}
                    >
                        운송요청으로 이동
                    </Button>
                </ToolBox>
            </ContentHeader>
            <ContentBody style={{
                    paddingLeft:5, whiteSpace: 'nowrap', overflowX: 'auto'
                }}
            >
                {
                    transPlanTsrListData.records.map(item=>{
                        return(
                            <TransPlanTsrItem key={item.hocha}
                                item={item}
                                onCompleteMutation={onCompleteTsrMutation}
                            />
                        )
                    })
                }
                {
                    transPlanTsrListData.records.length === 0 &&
                    <Box style={{
                        display:'flex',
                        flexDirection:'column',
                        justifyContent:'center',
                        alignItems:'center',
                        paddingTop:50
                    }}>
                        <img src={StoreHouseSvg} alt="_" height="320" />
                        <div style={{marginTop:20, fontSize:'2rem', color:'#fa3'}}>
                            해당 점포로의 배차내역이 없습니다.
                        </div>
                    </Box>
                }
            </ContentBody>
        </ContentConainer>
    );
}
